/**
 * Coerces a date-ish string into the `YYYY-MM-DD` format the backend / Sterling
 * expects. Tolerates:
 *   - already-correct `YYYY-MM-DD`
 *   - ISO with time/zone like `2026-01-06T15:14:00` or `2026-01-06T00:00:00Z`
 *   - locale strings parseable by `new Date(...)` (falls back to UTC date parts)
 *
 * Returns `undefined` for blanks or strings the runtime can't parse, so callers
 * can skip the field rather than send garbage.
 */
export function toYmd(input: unknown): string | undefined {
  if (typeof input !== 'string') return undefined;
  const trimmed = input.trim();
  if (!trimmed) return undefined;

  // Fast path: anything that starts with YYYY-MM-DD just takes the first 10
  // chars verbatim. This handles plain dates and any ISO timestamp.
  const ymdPrefix = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymdPrefix) return `${ymdPrefix[1]}-${ymdPrefix[2]}-${ymdPrefix[3]}`;

  // Fallback: hand it to Date(). Use UTC parts so timezone doesn't drift the
  // calendar day.
  const d = new Date(trimmed);
  if (!Number.isNaN(d.getTime())) {
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  return undefined;
}
